
import { defineComponent, PropType } from 'vue'

import { AxiosResponse } from 'axios'
import Badge from 'primevue/badge'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import DataPreparationDescriptor from './DataPreparationDescriptor.json'
import Divider from 'primevue/divider'
import Dropdown from 'primevue/dropdown'
import Sidebar from 'primevue/sidebar'
import OverlayPanel from 'primevue/overlaypanel'
import Listbox from 'primevue/listbox'

import Menu from 'primevue/menu'

import DataPreparationDialog from '@/modules/workspace/dataPreparation/DataPreparationDialog.vue'
import DataPreparationSaveDialog from '@/modules/workspace/dataPreparation/DataPreparationSaveDialog.vue'
import { IDataPreparationColumn } from '@/modules/workspace/dataPreparation/DataPreparation'
import KnCalculatedField from '@/components/functionalities/KnCalculatedField/KnCalculatedField.vue'
import DataPreparationSimpleDescriptor from '@/modules/workspace/dataPreparation/DataPreparationSimple/DataPreparationSimpleDescriptor.json'
import DataPreparationSplitDescriptor from '@/modules/workspace/dataPreparation/DataPreparationCustom/DataPreparationSplitDescriptor.json'
import calculatedFieldDescriptor from '@/modules/workspace/dataPreparation/DataPreparationCalculatedFieldDescriptor.json'

import { Client } from '@stomp/stompjs'
import { formatDateWithLocale } from '@/helpers/commons/localeHelper'

export default defineComponent({
    name: 'data-preparation-detail',
    props: {
        id: Number,
        transformations: Array as PropType<any[]>,
        existingProcessId: String,
        existingInstanceId: String,
        existingDataset: String
    },
    components: { Listbox, KnCalculatedField, Badge, Column, DataPreparationDialog, DataPreparationSaveDialog, DataTable, Divider, Dropdown, OverlayPanel, Sidebar, Menu },

    data() {
        return {
            descriptor: DataPreparationDescriptor,
            loading: 0,
            datasetData: Array<any>(),
            displayDataPreparationDialog: false as boolean,
            selectedProduct: null,
            visibleRight: false as boolean,
            visibility: false as boolean,
            selectedTransformation: null,
            showSaveDialog: false as boolean,
            showCFDialog: false as boolean,
            columns: [] as IDataPreparationColumn[],
            col: null,
            descriptorTransformations: Array<any>(),
            dataset: {} as any,
            simpleDescriptor: DataPreparationSimpleDescriptor,
            splitDescriptor: DataPreparationSplitDescriptor,
            client: {} as any,
            cfDescriptor: calculatedFieldDescriptor,
            instanceId: '' as string,
            processId: '' as string,
            readOnly: false as boolean,
            preparedDsMeta: {},
            progressMode: 'indeterminate',
            cfType: ''
        }
    },

    async created() {
        this.loading++
        this.descriptorTransformations = Object.assign([], this.descriptor.transformations)

        await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + '1.0/datasets/dataset/id/' + this.id).then((response: AxiosResponse<any>) => {
            this.dataset = response.data[0]
        })
        if (this.dataset) {
            await this.initDsMetadata()
            this.initTransformations()

            var url = new URL(window.location.origin)
            url.protocol = url.protocol.replace('http', 'ws')
            var uri = url + 'knowage-data-preparation/ws?' + process.env.VUE_APP_DEFAULT_AUTH_HEADER + '=' + localStorage.getItem('token')
            this.client = new Client({
                brokerURL: uri,
                connectHeaders: {},
                heartbeatIncoming: 4000,
                heartbeatOutgoing: 4000
            })

            this.client.onConnect = () => {
                this.client.subscribe(
                    '/user/queue/preview',
                    (message) => {
                        // called when the client receives a STOMP message from the server
                        if (message.body) {
                            this.updateTable(message.body)
                        } else {
                            console.log('got empty message')
                        }
                        this.loading--
                    },
                    {
                        dsId: this.dataset.id
                    }
                )

                this.client.subscribe('/user/queue/error', (error) => {
                    // called when the client receives a STOMP message from the server
                    if (error.body) {
                        let message = JSON.parse(error.body)
                        this.$store.commit('setError', { title: 'Error', msg: message.message })
                    } else {
                        this.$store.commit('setError', { title: 'Error' })
                    }
                    if (this.dataset.config && this.dataset.config.transformations?.length > 0) this.dataset.config.transformations.splice(-1)
                    this.loading--
                })

                this.client.subscribe(
                    '/user/queue/prepare',
                    (message) => {
                        // called when the client receives a STOMP message from the server
                        if (message.body) {
                            let avroJobResponse = JSON.parse(message.body)
                            if (avroJobResponse.statusOk) this.$store.commit('setInfo', { title: 'Dataset prepared successfully' })
                            else this.$store.commit('setError', { title: 'Cannot prepare dataset', msg: avroJobResponse.errorMessage })
                            //TODO: refresh data?
                        } else {
                            this.$store.commit('setError', { title: 'Websocket error', msg: 'got empty message' })
                        }
                    },
                    {
                        dsId: this.dataset.id
                    }
                )

                if (this.transformations) {
                    this.client.publish({ destination: '/app/preview', headers: { dsId: this.dataset.id }, body: JSON.stringify(this.dataset.config.transformations) })
                }
            }

            this.client.activate()
        }
    },

    methods: {
        getFormattedDate(date: any, format: any) {
            return formatDateWithLocale(date, format, true)
        },
        getProgressValue() {
            if (this.dataset.config && this.dataset.config.transformations && this.dataset.config.transformations.length && this.dataset.config.transformations.length > 1) {
                this.progressMode = ''
                let tot = this.dataset.config.transformations.length

                return (100 * (tot - this.loading)) / tot
            }

            this.progressMode = 'indeterminate'

            return 0
        },
        reverseTransformations() {
            if (this.dataset.config && this.dataset.config.transformations) {
                let transformations = [...this.dataset.config.transformations]
                return transformations.reverse()
            }

            return []
        },
        cancelCFDialog(): void {
            this.selectedTransformation = null
            this.showCFDialog = false
            this.cfType = ''
        },
        saveCFDialog(t): void {
            let convertedTransformation = this.convertCFTransformation(t)
            this.handleTransformation(convertedTransformation)
            this.showCFDialog = false
        },
        convertCFTransformation(t) {
            let transformation = { parameters: [] as Array<any>, type: 'calculatedField' }
            let par = { columns: [] as Array<any> }
            Object.keys(t).forEach((key) => {
                if (key === 'column') par.columns.push(t[key].header)
                else par[key] = t[key]
            })
            if (this.cfDescriptor.availableTypes) {
                par['type'] = this.cfType
            }
            transformation.parameters.push(par)
            return transformation
        },
        calculateDisabledProperty(menu): Boolean {
            if (this.loading > 0) return true
            let disabled = false
            if (menu.type === 'advancedFilter') {
                if (!this.dataset.config) disabled = true
                else disabled = this.dataset.config.transformations.filter((x) => x.type === 'filter').length < 2
            }
            return disabled
        },
        changeAlias(col): void {
            if (col.editing) delete col.editing
            else col.editing = true
        },
        closeTemplate(): void {
            this.$router.go(-1)
        },
        refreshOriginalDataset(): void {
            // launch avro export job
            this.$http
                .post(
                    process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/data-preparation/prepare/${this.dataset.id}`,
                    {},
                    {
                        headers: {
                            Accept: 'application/json, text/plain, */*',
                            'Content-Type': 'application/json;charset=UTF-8'
                        }
                    }
                )
                .then(() => {
                    this.$store.commit('setInfo', {
                        title: this.$t('workspace.myData.isPreparing')
                    })
                })
                .catch(() => {})

            // listen on websocket for avro export job to be finished
            this.client.publish({ destination: '/app/prepare', body: this.dataset.id })
        },
        openTransformationDetail(t) {
            this.readOnly = true
            let selectedTransformation = this.descriptorTransformations.filter((x) => x.name == t.type)[0]
            selectedTransformation['parameters'] = []
            let param = t.parameters[0]
            Object.keys(param).forEach((key) => {
                let obj = {}
                obj['name'] = key
                if (key == 'columns') {
                    let value = [] as Array<any>
                    for (let i = 0; i < param[key].length; i++) {
                        let col = this.columns.filter((x) => x.fieldAlias.toUpperCase() === param[key][i].toUpperCase())[0]
                        value.push(col)
                    }
                    obj['value'] = value
                } else {
                    obj['value'] = param[key]
                }

                if (t.type === 'calculatedField' && this.cfDescriptor.availableTypes) {
                    this.cfType = t.parameters[0].type
                }

                selectedTransformation['parameters'].push(obj)
            })

            if (t.type == 'filter' || t.type == 'split') {
                let col = this.columns.filter((x) => x.fieldAlias.toUpperCase() === t.parameters[0].columns[0].toUpperCase())[0]
                this.callFunction(selectedTransformation, col)
            } else this.callFunction(selectedTransformation, undefined)
        },
        getTransformationsMenu(col: IDataPreparationColumn): Array<any> {
            return this.descriptorTransformations
                .filter((x) => x.editColumn && !x.hidden)
                .filter((x) => {
                    if (x.incompatibleDataTypes) return !x.incompatibleDataTypes?.includes(col.Type)
                    return true
                })
        },
        initTransformations(): void {
            if (this.transformations) {
                if (!this.dataset.config) this.dataset.config = {}
                this.dataset.config.transformations = this.transformations
                this.loading++
            }
        },
        async initDsMetadata() {
            if (this.existingProcessId) this.processId = this.existingProcessId
            if (this.existingInstanceId) this.instanceId = this.existingInstanceId
            if (this.existingDataset) {
                let dsMeta = JSON.parse(this.existingDataset)
                let tmp = {}
                tmp['label'] = dsMeta.label
                tmp['name'] = dsMeta.name
                tmp['description'] = dsMeta.description
                tmp['id'] = dsMeta.id
                await this.$http.get(process.env.VUE_APP_DATA_PREPARATION_PATH + '1.0/process/by-destination-data-set/' + dsMeta.id).then((response: AxiosResponse<any>) => {
                    let instance = response.data.instance
                    if (instance.config) {
                        tmp['config'] = instance.config
                    }
                })

                this.preparedDsMeta = tmp
            }
        },
        getColHeader(metadata: Array<any>, idx: Number): string {
            let columnMapping = 'column_' + idx
            let toReturn = metadata.filter((x) => x.mappedTo == columnMapping)[0].alias
            return toReturn
        },
        callFunction(transformation: any, col): void {
            if (transformation.name === 'changeType' || transformation.name === 'split') {
                let parsArray = transformation.name === 'changeType' ? this.simpleDescriptor[transformation.name].parameters : this.splitDescriptor.parameters
                for (var i = 0; i < parsArray.length; i++) {
                    let element = parsArray[i]
                    if (element.name === 'destType' || element.name === 'destType1' || element.name === 'destType2') {
                        element.availableOptions = col ? this.getCompatibilityType(col) : this.descriptor.compatibilityMap['all'].values

                        element.availableOptions.forEach((element) => {
                            element.label = this.removePrefixFromType(element.label)
                        })
                    }
                }

                /* this.handleTransformation(transformation) */
                this.selectedTransformation = transformation
                if (col) this.col = col.header
            } else if (transformation.name === 'drop' && col) {
                this.$confirm.require({
                    message: this.$t('common.toast.deleteMessage'),
                    header: this.$t('common.toast.deleteTitle'),
                    icon: 'pi pi-exclamation-triangle',
                    accept: () => {
                        let par = this.simpleDescriptor[transformation.name].parameters[0]
                        par.value = col.header
                        transformation.parameters = []
                        transformation.parameters.push(par)
                        let toReturn = { parameters: [] as Array<any>, type: 'drop' }
                        let obj = { columns: [] as Array<any> }
                        obj.columns.push(col.header)

                        toReturn.parameters.push(obj)

                        this.handleTransformation(toReturn)
                    }
                })
            } else {
                this.selectedTransformation = transformation
                if (col) this.col = col.header
                if (transformation.name === 'calculatedField') this.showCFDialog = true
            }
        },
        handleTransformation(t: any): void {
            if (!this.dataset.config) this.dataset.config = {}
            if (!this.dataset.config.transformations) this.dataset.config.transformations = []
            this.dataset.config.transformations.push(t)
            this.loading++
            this.client.publish({ destination: '/app/preview', headers: { dsId: this.dataset.id }, body: JSON.stringify(this.dataset.config.transformations) })
        },
        toggleSidebarVisibility() {
            this.visibleRight = true
        },
        deleteTransformation(index: number): void {
            if (index) this.dataset.config.transformations.splice(index, 1)
            else this.dataset.config.transformations.splice(-1) // remove last element
            this.loading++
            this.client.publish({ destination: '/app/preview', headers: { dsId: this.dataset.id }, body: JSON.stringify(this.dataset.config.transformations) })
        },
        getCompatibilityType(col: IDataPreparationColumn): void {
            return this.descriptor.compatibilityMap[col.Type].values
        },
        toggle(event: Event, trOp: string): void {
            // eslint-disable-next-line
            // @ts-ignore
            this.$refs[trOp].toggle(event)
        },
        getMenuForToolbar(): Array<any> {
            let tmp = this.descriptorTransformations
                .filter((x) => x.toolbar && !x.hidden)
                .sort(function(a, b) {
                    if (a.position > b.position) return 1
                    if (a.position < b.position) return -1
                    return 0
                })

            let menu = [] as Array<any>
            if (tmp.length > 0) {
                let type = tmp[0].category
                menu.push(tmp[0])

                for (let i = 1; i < tmp.length; i++) {
                    if (type !== tmp[i].category) {
                        type = tmp[i].category
                        menu.push('divider')
                    }
                    menu.push(tmp[i])
                }
            }
            return menu
        },
        removePrefixFromType(type: String): String {
            let splitted = type.split('.', -1)

            return splitted.length > 0 ? splitted[splitted.length - 1] : splitted[0]
        },
        saveDataset(): void {
            this.showSaveDialog = true
        },
        translateRoles() {
            let translatedRoles = this.descriptor.roles
            translatedRoles.forEach((x) => (x.label = this.$t(x.label)))
            return translatedRoles
        },
        switchEditMode(col) {
            col.edit = !col.edit
        },
        updateReadOnly(state): void {
            this.readOnly = state
        },
        updateInstanceId(iid): void {
            this.instanceId = iid
        },
        updateprocessId(pid): void {
            this.processId = pid
        },
        updateTable(message) {
            let response = JSON.parse(message)
            // set headers
            let metadata = response.metadata.columns
            this.columns = []
            for (let i = 0; i < metadata.length; i++) {
                let obj = {} as IDataPreparationColumn
                obj.Type = metadata[i].type
                obj.disabled = false
                obj.fieldAlias = metadata[i].alias
                obj.fieldType = metadata[i].fieldType
                obj.header = metadata[i].name
                this.columns.push(obj)
            }
            //set data rows
            this.datasetData = []
            response.rows.forEach((row) => {
                let obj = {}
                for (let i = 0; i < row.length; i++) {
                    let colHeader = this.getColHeader(metadata, i)
                    obj[colHeader] = row[i]
                }
                this.datasetData.push(obj)
            })
        }
    },
    unmounted() {
        if (Object.keys(this.client).length > 0) {
            this.client.deactivate()
            this.client = {}
        }
    }
})
